import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Alert, Button, Dropdown, Empty, message, Input, Select, Tooltip } from 'antd';
import { EllipsisOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuInfo } from 'rc-menu/lib/interface';
import dayjs from 'dayjs';
import { Content, PageSectionHeader } from '@/components';
import { NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import { useListDocumentsQuery, useRevokeDocumentMutation, type IoDocument } from '@/api';
import { DocumentHistory } from './components/DocumentHistory';
import { pandaDocErrorMessage } from '@/utils/pandaDoc';
import { pipedriveDealUrl } from '@/utils/pipedrive';
import { IoPreviewDrawer } from './components/IoPreviewDrawer';
import { CancelIoModal } from './components/CancelIoModal';
import { PrismaModal } from './components/PrismaModal';
import {
  StatusTag,
  StatusTagGroup,
  TableActions,
  TableContainer,
  Toolbar
} from './Io.styles';

const DATE_FORMAT = 'DD MMM YYYY, HH:mm';

/** BoldSign reports epoch seconds. */
const formatDate = (value?: number): string =>
  value ? dayjs.unix(value).format(DATE_FORMAT) : 'N/A';

/**
 * Maps a document status onto one of the five visual states.
 *
 * Ordered most-specific first: BoldSign reports a document that has been opened
 * but not signed as `InProgress`, so "viewed" has to be tested before the
 * in-progress catch-all or it would never be reached.
 */
const statusVariant = (status = '', superseded = false): string => {
  /* Checked before the status words: a replaced IO keeps whatever status it
     ended on — a signed one still reports completed at the provider forever —
     so testing status first would render it identically to the revision that
     replaced it. */
  if (superseded) return 'superseded';
  const s = status.toLowerCase();
  if (s.includes('completed') || s.includes('signed')) return 'completed';
  if (s.includes('declined') || s.includes('revoked') || s.includes('expired')) return 'error';
  if (s.includes('viewed')) return 'viewed';
  if (s.includes('progress') || s.includes('sent')) return 'sent';
  return 'draft';
};

/** i18n key for a provider status, so "InProgress" is not shown verbatim. */
const statusLabelKey = (status = '', superseded = false): string => {
  if (superseded) return 'ioStatusSuperseded';
  const s = status.toLowerCase();
  if (s.includes('completed') || s.includes('signed')) return 'ioStatusCompleted';
  if (s.includes('declined')) return 'ioStatusDeclined';
  if (s.includes('revoked')) return 'ioStatusRevoked';
  if (s.includes('expired')) return 'ioStatusExpired';
  if (s.includes('viewed')) return 'ioStatusViewed';
  if (s.includes('progress') || s.includes('sent')) return 'ioStatusSent';
  return 'ioStatusDraft';
};

/**
 * Which documents offer a cancel action.
 *
 * Anything still in flight can be withdrawn, and a signed IO can be cancelled
 * too — a booking is called off after signature often enough that the
 * alternative is doing it in the provider and leaving NRVE out of date. Only
 * documents already finished with (cancelled, declined, expired) are excluded.
 */
const isCancellable = (status = ''): boolean =>
  ['sent', 'viewed', 'signed', 'completed'].includes(statusVariant(status));

/** Label prefix the raise stamps on every document, carrying its plan id. */
const PLAN_LABEL_PREFIX = 'mediaPlanId:';

/**
 * The media plan a document was raised against.
 *
 * Read from the labels rather than held as a field: the list comes from the
 * signing provider, which knows only what was stamped on the document when it
 * was raised.
 */
const planIdOf = (doc?: IoDocument): string | undefined =>
  (doc?.labels ?? [])
    .find((label) => label.startsWith(PLAN_LABEL_PREFIX))
    ?.slice(PLAN_LABEL_PREFIX.length) || undefined;

/**
 * Whether this IO's booking is still waiting on a signed client order.
 *
 * Shown alongside the status rather than replacing it: the document really is
 * signed, and the thing worth flagging is that its line items have not reached
 * the booking database despite that.
 */
const isAwaitingClientOrder = (doc?: IoDocument): boolean =>
  Boolean(doc?.requiresClientOrder) && doc?.clientOrderStatus !== 'received';

/** This IO's booking was confirmed by a signed client order we hold. */
const hasClientOrder = (doc?: IoDocument): boolean =>
  Boolean(doc?.requiresClientOrder) && doc?.clientOrderStatus === 'received';

/** A document a later revision has replaced. */
const isSuperseded = (doc?: { supersededAt?: string }): boolean => Boolean(doc?.supersededAt);

/** A signed IO is an executed agreement, so its cancellation is flagged. */
const isSignedStatus = (status = ''): boolean =>
  ['signed', 'completed'].includes(statusVariant(status));

const Io: FC = () => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { data, isFetching, isError, error: listError } = useListDocumentsQuery({ pageSize: 100 });
  const [revokeDocument, { isLoading: isRevoking }] = useRevokeDocumentMutation();

  const [selected, setSelected] = useState<IoDocument | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<IoDocument | null>(null);
  const [prismaFor, setPrismaFor] = useState<IoDocument | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const allDocuments = useMemo(() => data?.result ?? [], [data]);

  /** Status values actually present, so the filter never offers an empty option. */
  const statusOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const doc of allDocuments) {
      const superseded = isSuperseded(doc);
      const variant = statusVariant(doc.status, superseded);
      if (!seen.has(variant)) seen.set(variant, t(statusLabelKey(doc.status, superseded)));
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [allDocuments, t]);

  const documents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allDocuments.filter((doc) => {
      const matchesTerm = !term || (doc.messageTitle || '').toLowerCase().includes(term);
      const matchesStatus = !statusFilter || statusVariant(doc.status, isSuperseded(doc)) === statusFilter;
      return matchesTerm && matchesStatus;
    });
  }, [allDocuments, search, statusFilter]);

  const isFiltered = Boolean(search.trim() || statusFilter);

  const handleRevoke = async (documentId: string, cancelMessage: string) => {
    setRevokingId(documentId);
    try {
      await revokeDocument({ documentId, message: cancelMessage }).unwrap();
      setCancelling(null);
      message.success(t('ioRevoked'));
    } catch (err) {
      message.error(
        pandaDocErrorMessage(err, {
          sessionExpired: t('ioSessionExpired'),
          integrationUnavailable: t('ioIntegrationUnavailable'),
          fallback: t('ioRevokeFailed')
        })
      );
    } finally {
      setRevokingId(null);
    }
  };

  const columns: ColumnsType<IoDocument> = useMemo(
    () => [
      {
        title: t('name'),
        dataIndex: 'messageTitle',
        key: 'messageTitle',
        sorter: (a, b) => (a.messageTitle || '').localeCompare(b.messageTitle || '')
      },
      {
        title: t('status', { ns: NAMESPACE.COMMON }),
        dataIndex: 'status',
        key: 'status',
        render: (status: string, record: IoDocument) => {
          const superseded = isSuperseded(record);
          return (
            <StatusTagGroup>
              <StatusTag className={`status-${statusVariant(status, superseded)}`}>
                {t(statusLabelKey(status, superseded))}
              </StatusTag>
              {/* A signed IO whose booking is held reads as finished without
                  this, which is the case most likely to be missed. */}
              {isAwaitingClientOrder(record) && !superseded && (
                <StatusTag className="status-awaiting-order">
                  {t('clientOrderAwaited')}
                </StatusTag>
              )}
              {/* Marked once confirmed too, not only while outstanding: these
                  bookings were confirmed by an order we signed rather than by
                  the client signing this IO, and that is worth knowing when
                  reading the row later. */}
              {hasClientOrder(record) && !superseded && (
                <StatusTag className="status-client-order">
                  {t('clientOrderHeld')}
                </StatusTag>
              )}
            </StatusTagGroup>
          );
        }
      },
      {
        /* The two facts that identify an IO at a glance. Previously only in
           the preview drawer, which meant opening each document to find the
           one you were after. */
        title: t('ioBudget'),
        dataIndex: 'totalBudget',
        key: 'totalBudget',
        align: 'right' as const,
        render: (value?: number, record?: IoDocument) =>
          value != null ? formatMoney(value, record?.currency) : '—',
        sorter: (a, b) => (a.totalBudget ?? 0) - (b.totalBudget ?? 0)
      },
      {
        title: t('ioFlight'),
        key: 'flight',
        render: (_: unknown, record: IoDocument) =>
          record.startDate || record.endDate
            ? [record.startDate, record.endDate]
                .map((date) => (date ? dayjs(date).format('DD MMM YY') : '—'))
                .join(' – ')
            : '—',
        /* Sorted on the start: a list of IOs is read by when the campaign runs,
           not by when it ends. */
        sorter: (a, b) =>
          dayjs(a.startDate ?? 0).valueOf() - dayjs(b.startDate ?? 0).valueOf()
      },
      {
        /* The PipeDrive deal, so an IO can be tied back to the deal without
           opening the plan — and opens it, in a new tab, since the IO list is
           read while cross-checking a deal rather than on the way out of it. */
        title: t('ioDealId'),
        dataIndex: 'dealId',
        key: 'dealId',
        align: 'right' as const,
        render: (value?: number) => {
          const url = pipedriveDealUrl(value);
          if (!url) return '\u2014';
          return (
            <a href={url} target="_blank" rel="noreferrer noopener">
              {value}
            </a>
          );
        },
        sorter: (a, b) => (a.dealId ?? 0) - (b.dealId ?? 0)
      },
      {
        title: t('createdAt'),
        dataIndex: 'createdDate',
        key: 'createdDate',
        render: formatDate,
        sorter: (a, b) => (a.createdDate ?? 0) - (b.createdDate ?? 0),
        defaultSortOrder: 'descend' as const
      },
      {
        title: t('modifiedAt'),
        dataIndex: 'activityDate',
        key: 'activityDate',
        render: formatDate,
        sorter: (a, b) => (a.activityDate ?? 0) - (b.activityDate ?? 0)
      },
      {
        title: t('actions', { ns: NAMESPACE.COMMON }),
        key: 'actions',
        width: 140,
        /* Preview stays inline; cancelling -- which revokes a contract, often
           one the client has already signed -- moves behind the overflow menu.
           Side by side at equal weight, the destructive action was one
           mis-click away from the harmless one on every row. */
        render: (_, record) => (
          <TableActions onClick={(e) => e.stopPropagation()}>
            {/* An icon, with the name kept for assistive tech and as a
                tooltip: the label was the widest thing in a narrow actions
                column and said the same thing on every row. */}
            <Tooltip title={t('preview')}>
              <Button
                type="text"
                aria-label={t('preview')}
                icon={<EyeOutlined style={{ fontSize: 16 }} />}
                onClick={() => setSelected(record)}
              />
            </Tooltip>
            {isCancellable(record.status) && !isSuperseded(record) && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    /* Offered where a client order is actually required and
                       still outstanding — not on every signed IO, which is
                       most of them and none of which need one.
                       
                       Deliberately not gated on our IO being signed: on these
                       campaigns the client never signs it. We raise one so the
                       schedule reaches the booking database, and signing their
                       order is what confirms the booking, so waiting for a
                       signature would hide the action on exactly the documents
                       that need it. A plan id is still required, since that is
                       what the confirmation books. */
                    /* Also offered once confirmed, so the signed order can be
                       opened from the IO it confirms rather than being
                       reachable only during the upload that attached it. */
                    ...((isAwaitingClientOrder(record) || hasClientOrder(record)) &&
                    !isSuperseded(record) &&
                    planIdOf(record)
                      ? [
                          {
                            key: 'prisma',
                            label: hasClientOrder(record)
                              ? t('prismaViewAction')
                              : t('prismaConfirmAction'),
                            onClick: ({ domEvent }: MenuInfo) => {
                              domEvent.stopPropagation();
                              setPrismaFor(record);
                            }
                          }
                        ]
                      : []),
                    {
                      key: 'cancel',
                      danger: true,
                      label: t('ioCancelAction'),
                      onClick: ({ domEvent }) => {
                        domEvent.stopPropagation();
                        setCancelling(record);
                      }
                    }
                  ]
                }}
              >
                <Button
                  type="text"
                  aria-label={t('more', { ns: NAMESPACE.COMMON })}
                  loading={isRevoking && revokingId === record.documentId}
                  icon={<EllipsisOutlined style={{ fontSize: 18 }} />}
                />
              </Dropdown>
            )}
          </TableActions>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRevoking, revokingId]
  );

  return (
    <>
      <PageSectionHeader>{t('insertionOrders')}</PageSectionHeader>
      <Content>
        {isError ? (
          <Alert
            type="error"
            role="alert"
            showIcon
            message={pandaDocErrorMessage(listError, {
              sessionExpired: t('ioSessionExpired'),
              integrationUnavailable: t('ioIntegrationUnavailable'),
              fallback: t('ioPreviewFailed')
            })}
          />
        ) : (
          <TableContainer>
            <Toolbar>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('ioSearchPlaceholder')}
                aria-label={t('ioSearchPlaceholder')}
              />
              <Select
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
                placeholder={t('ioFilterByStatus')}
                aria-label={t('ioFilterByStatus')}
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
              />
            </Toolbar>
            <Table<IoDocument>
              rowKey="documentId"
              columns={columns}
              dataSource={documents}
              loading={isFetching}
              expandable={{
                // Fetched per row on expand: most rows are never opened, and a
                // history call for every document would be wasted work.
                expandedRowRender: (document) => (
                  <DocumentHistory documentId={document.documentId} />
                )
              }}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100],
                showTotal: (total) => t('ioTotalDocuments', { count: total })
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={isFiltered ? t('ioNoResults') : t('ioListEmpty')}
                  />
                )
              }}
              onRow={(record) => ({ onClick: () => setSelected(record) })}
            />
          </TableContainer>
        )}
      </Content>
      <CancelIoModal
        document={cancelling}
        isSigned={isSignedStatus(cancelling?.status)}
        isSubmitting={isRevoking}
        onCancel={() => setCancelling(null)}
        onConfirm={(reason) => cancelling && handleRevoke(cancelling.documentId, reason)}
      />
      <PrismaModal
        mediaPlanId={prismaFor ? planIdOf(prismaFor) ?? null : null}
        documentName={prismaFor?.messageTitle}
        existingFileName={prismaFor?.clientOrderFileName}
        onClose={() => setPrismaFor(null)}
      />
      <IoPreviewDrawer document={selected} onClose={() => setSelected(null)} />
    </>
  );
};

export default Io;
